import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { createPlayer, FOOT_OFFSET, type PlayerHandle } from '../map/player';
import { TILE } from '../map/world';
import type { Appearance } from '../character/compositor';
import { CANVAS_FONT } from '../fonts';
import { pxOfTileCenter, type FootAnchor } from './coords';

/**
 * An arena hero = the SAME animated character the farm uses (createPlayer): real
 * walk/idle cycles, farm movement speed, wall collision, proper armour layering — not
 * a static sliding frame. A nickname plate + HP bar float above it.
 *
 * Movement is driven exactly like the farm: setPath()/setKeys() feed the handle, and
 * update(dt, clampPos, blocked) animates + moves + collides. Positions are world
 * pixels (TILE = 48), so the whole arena runs at farm scale.
 */

const NAME_STYLE = new TextStyle({
  fontFamily: CANVAS_FONT,
  fontSize: 11,
  fill: 0xffffff,
  stroke: { color: 0x000000, width: 3 },
});

export interface ArenaHero {
  handle: PlayerHandle;      // the farm player handle (sprite + animation + movement)
  overlay: Container;        // nickname plate + HP bar, follows the hero
  id: string;
  nickname: string;
  isPlayer: boolean;
  get x(): number;           // world px
  get y(): number;
  hp: number;
  maxHp: number;
  alive: boolean;
  setHp(hp: number): void;
  setTargetPx(px: number, py: number): void;
  setPathPx(points: { x: number; y: number }[]): void;
  setKeys(vx: number, vy: number): void;
  stop(): void;
  /** Cancel an in-progress auto-attack swing so movement resumes instantly. */
  interrupt(): void;
  /** Advance animation + movement one frame (farm logic). */
  update(dt: number, clampPos: (x: number, y: number) => [number, number], blocked: (wx: number, wy: number) => boolean): void;
  kill(): Promise<void>;
  syncOverlay(): void;
  destroy(): void;
}

/** The arena's tile<->pixel anchor: farm tiles, feet FOOT_OFFSET above the anchor point. */
export const ARENA_ANCHOR: FootAnchor = { tile: TILE, footOffset: FOOT_OFFSET };

export async function createArenaHero(opts: {
  id: string; nickname: string; isPlayer: boolean; tx: number; ty: number;
  appearance: Appearance; maxHp: number;
}): Promise<ArenaHero> {
  // Spawn through the SHARED anchor (arena/coords.ts) rather than a hand-tuned
  // `ty + 0.9`: the feet land in the centre of the spawn tile, which is exactly
  // where a path waypoint for that tile now puts them too. The two conventions
  // used to differ by most of a tile, and every range/safe-zone test inherited
  // the disagreement.
  const spawn = pxOfTileCenter(opts.tx, opts.ty, ARENA_ANCHOR);
  const handle = await createPlayer(opts.appearance, spawn.x, spawn.y);
  // §6.6: every arena hero carries a visible blade (the arena is a melee fight).
  // The pack has no dagger sheet, so this is the sword — the closest melee art.
  handle.setHeld('sword');

  const overlay = new Container();
  const hpBar = new Graphics();
  const plate = new Text({ text: opts.nickname, style: NAME_STYLE });
  plate.anchor.set(0.5, 1);
  plate.y = -TILE * 1.15;
  const ring = new Graphics();
  // Draw the ring at the hero's FEET, not at the sprite's position point — the feet
  // render ~FOOT_OFFSET above the position point, so a ring at y=0 floated well below
  // the character. -FOOT_OFFSET puts it right under their boots.
  if (opts.isPlayer) ring.ellipse(0, -FOOT_OFFSET, TILE * 0.42, TILE * 0.2).stroke({ color: 0xffd75e, width: 3, alpha: 0.9 });
  overlay.addChild(ring, hpBar, plate);

  let hp = opts.maxHp;
  let alive = true;
  const drawHp = () => {
    const w = TILE * 0.9, pct = Math.max(0, hp / opts.maxHp);
    hpBar.clear()
      .rect(-w / 2, -TILE * 0.95, w, 4).fill({ color: 0x000000, alpha: 0.6 })
      .rect(-w / 2, -TILE * 0.95, w * pct, 4).fill({ color: pct > 0.5 ? 0x6bd06b : pct > 0.25 ? 0xe0c020 : 0xe04040 });
  };
  drawHp();

  const self: ArenaHero = {
    handle, overlay, id: opts.id, nickname: opts.nickname, isPlayer: opts.isPlayer,
    get x() { return handle.x; }, get y() { return handle.y; },
    get hp() { return hp; }, maxHp: opts.maxHp,
    get alive() { return alive; },
    setHp(v) { hp = Math.max(0, Math.min(opts.maxHp, v)); drawHp(); },
    setTargetPx(px, py) { handle.setTarget(px, py); },
    setPathPx(points) { handle.setPath(points); },
    setKeys(vx, vy) { handle.setKeys(vx, vy); },
    stop() { handle.setKeys(0, 0); handle.setPath([]); },
    interrupt() { handle.interruptAction(); },
    update(dt, clampPos, blocked) { handle.update(dt, clampPos, blocked); },
    // §6.7: on death, DESPAWN — a dead hero is no longer ticked (the arena skips it
    // in applyMovement), so the old code left a frozen grey corpse that never
    // animated and still sat on the field. Remove the character + overlay at once so
    // there's no lingering body blocking movement and the live counter drops
    // immediately. The handle is fully freed at match teardown via the heroes list.
    async kill() {
      if (!alive) return; alive = false;
      overlay.visible = false;
      handle.sprite.visible = false;
      handle.sprite.parent?.removeChild(handle.sprite);
      overlay.parent?.removeChild(overlay);
    },
    syncOverlay() { overlay.x = handle.x; overlay.y = handle.y; overlay.zIndex = handle.y + 1; handle.sprite.zIndex = handle.y; },
    destroy() { overlay.destroy({ children: true }); handle.destroy(); },
  };
  self.syncOverlay();
  return self;
}
